import { useState, useRef, useCallback, useEffect, useMemo, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { filterItems, mapApiToItem } from '@/api/item.api';
import { getSuppliers, type PartyResponse } from '@/api/party.api';
import { jobWorkService } from '@/services/jobWorkService';
import { complianceApi } from '@/api/compliance.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { getWarehousesForUser } from '@/api/warehouse.api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { serialApi } from '@/api/serial.api';
import { deriveSerialPrefix } from '@/utils/serialPrefix';

// ─── Config ───────────────────────────────────────────────────────────────────
const BASE_URL = (import.meta.env.VITE_API_URL as string);

function getToken(): string {
  return localStorage.getItem('token') ?? '';
}
function authHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getToken()}`,
  };
}

// ─── Types ────────────────────────────────────────────────────────────────────
type WarehouseType = 'MAIN' | 'TRANSIT' | 'RETURN' | 'COLD' | string;

interface Warehouse {
  id: string;
  name: string;
  type: WarehouseType;
  isActive: boolean;
}

export interface WarehouseResponse {
  id: string;
  company_id: string;
  name: string;
  type: WarehouseType;
  address: string;
  incharge_name: string;
  incharge_phone: string;
  incharge_user_id: string | null;
  color: string | null;
  is_active: boolean;
  is_primary?: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

const TYPE_BADGE: Record<string, { label: string; icon: string; cls: string }> = {
  MAIN: { label: 'Main', icon: 'ri-store-3-line', cls: 'bg-indigo-100 text-indigo-600' },
  TRANSIT: { label: 'Transit', icon: 'ri-truck-line', cls: 'bg-amber-100 text-amber-600' },
  RETURN: { label: 'Return', icon: 'ri-arrow-go-back-line', cls: 'bg-rose-100 text-rose-600' },
  COLD: { label: 'Cold', icon: 'ri-temp-cold-line', cls: 'bg-sky-100 text-sky-600' },
};

// ─── SearchableSelect ─────────────────────────────────────────────────────────
interface SelectOption {
  id: string;
  name: string;
  isActive?: boolean;
  allowDecimal?: boolean;
  defaultHsnCode?: string | null;
  defaultGstRate?: number | null;
}

interface SearchableSelectProps {
  value: string;
  onChange: (id: string) => void;
  options: SelectOption[];
  placeholder?: string;
  loading?: boolean;
  error?: boolean;
  navIndex?: number;
  disabled?: boolean;
}

function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Search…',
  loading,
  error,
  navIndex,
  disabled,
}: SearchableSelectProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((o) => o.id === value);

  const filtered = useMemo(() => {
    setHighlighted(-1);
    return query.trim()
      ? options.filter((o) => o.name.toLowerCase().includes(query.toLowerCase()))
      : options;
  }, [query, options]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
        setHighlighted(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (highlighted < 0 || !listRef.current) return;
    const item = listRef.current.children[highlighted + 1] as HTMLElement;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const handleSelect = (id: string) => {
    onChange(id);
    setOpen(false);
    setQuery('');
    setHighlighted(-1);
  };

  const handleOpen = () => {
    if (disabled || loading) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlighted((prev) => Math.min(prev + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlighted((prev) => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlighted === -1) {
          handleSelect('');
        } else if (filtered[highlighted]) {
          handleSelect(filtered[highlighted].id);
        }
        break;
      case 'Escape':
        setOpen(false);
        setQuery('');
        setHighlighted(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative" data-nav-index={navIndex}>
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled || loading}
        className={`w-full h-10 px-3 rounded-lg border text-sm text-left flex items-center justify-between bg-white focus:outline-none focus:ring-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
          error ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
        }`}
      >
        <span className={selected ? 'text-[#1e293b]' : 'text-[#94a3b8]'}>
          {loading ? 'Loading…' : selected ? selected.name : placeholder}
        </span>
        <i className={`ri-arrow-down-s-line text-[#94a3b8] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#e2e8f0] rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-[#f1f5f9]">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type to filter…"
                className="w-full h-8 px-3 pl-7 rounded-md border border-[#e2e8f0] text-sm text-[#1e293b] focus:outline-none focus:border-[#4f46e5]"
              />
              <i className="ri-search-line absolute left-2 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm" />
            </div>
          </div>

          <ul ref={listRef} className="max-h-48 overflow-y-auto">
            <li>
              <button
                type="button"
                onMouseDown={() => handleSelect('')}
                onMouseEnter={() => setHighlighted(-1)}
                className={`w-full px-3 py-2 text-sm text-left cursor-pointer transition-colors ${
                  highlighted === -1 ? 'bg-[#f1f5f9] text-[#64748b]' : 'text-[#94a3b8] hover:bg-[#f8fafc]'
                }`}
              >
                — None —
              </button>
            </li>

            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-sm text-center text-[#94a3b8]">
                No results for "{query}"
              </li>
            ) : (
              filtered.map((o, idx) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onMouseDown={() => handleSelect(o.id)}
                    onMouseEnter={() => setHighlighted(idx)}
                    className={`w-full px-3 py-2 text-sm text-left flex items-center justify-between cursor-pointer transition-colors ${
                      idx === highlighted
                        ? 'bg-[#eef2ff] text-[#4f46e5] font-medium'
                        : o.id === value
                          ? 'bg-[#eef2ff] text-[#4f46e5] font-medium'
                          : 'text-[#1e293b] hover:bg-[#f1f5f9]'
                    }`}
                  >
                    {o.name}
                    {o.id === value && <i className="ri-check-line text-[#4f46e5]" />}
                  </button>
                </li>
              ))
            )}
          </ul>

          <div className="px-3 py-1.5 border-t border-[#e2e8f0] text-[10px] text-[#94a3b8]">
            {filtered.length} of {options.length} shown
          </div>
        </div>
      )}
    </div>
  );
}

// ─── WarehouseSelect ──────────────────────────────────────────────────────────
interface WarehouseSelectProps {
  value: string;
  onChange: (id: string, name: string) => void;
  warehouses: Warehouse[];
  loading?: boolean;
  error?: boolean;
}

function WarehouseSelect({
  value,
  onChange,
  warehouses,
  loading,
  error,
}: WarehouseSelectProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = warehouses.find((w) => w.id === value);
  const selectedBadge = selected ? (TYPE_BADGE[selected.type] ?? null) : null;

  const filtered = query.trim()
    ? warehouses.filter(
        (w) =>
          w.name.toLowerCase().includes(query.toLowerCase()) ||
          w.type.toLowerCase().includes(query.toLowerCase()),
      )
    : warehouses;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (wh: Warehouse) => {
    onChange(wh.id, wh.name);
    setOpen(false);
    setQuery('');
  };
  const handleOpen = () => {
    if (loading) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleOpen}
        disabled={loading}
        className={`w-full h-10 px-3 rounded-lg border text-sm text-left flex items-center gap-2 bg-white hover:border-[#4f46e5] hover:bg-indigo-50 focus:outline-none focus:ring-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
          error ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:ring-[#4f46e5]/20'
        }`}
      >
        {selectedBadge ? (
          <div className={`w-6 h-6 flex items-center justify-center rounded-md shrink-0 ${selectedBadge.cls}`}>
            <i className={`${selectedBadge.icon} text-xs`} />
          </div>
        ) : (
          <div className="w-6 h-6 flex items-center justify-center rounded-md shrink-0 bg-slate-100">
            <i className="ri-store-3-line text-[#94a3b8] text-xs" />
          </div>
        )}

        <span className={`flex-1 truncate font-medium ${selected ? 'text-[#1e293b]' : 'text-[#94a3b8]'}`}>
          {loading ? 'Loading warehouses…' : selected ? selected.name : 'Select warehouse…'}
        </span>

        {selectedBadge && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${selectedBadge.cls}`}>
            {selectedBadge.label}
          </span>
        )}
        <i className={`ri-arrow-down-s-line text-[#94a3b8] shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#e2e8f0] rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-[#f1f5f9]">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setOpen(false);
                    setQuery('');
                  }
                  if (e.key === 'Enter' && filtered.length === 1) handleSelect(filtered[0]);
                }}
                placeholder="Search warehouse or type…"
                className="w-full h-8 px-3 pl-7 rounded-md border border-[#e2e8f0] text-sm text-[#1e293b] focus:outline-none focus:border-[#4f46e5]"
              />
              <i className="ri-search-line absolute left-2 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm" />
            </div>
          </div>

          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-sm text-center text-[#94a3b8]">
                No warehouses match "{query}"
              </li>
            ) : (
              filtered.map((wh) => {
                const badge = TYPE_BADGE[wh.type] ?? null;
                const isSelected = wh.id === value;
                return (
                  <li key={wh.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(wh)}
                      className={`w-full px-3 py-2.5 flex items-center gap-2.5 hover:bg-[#f8fafc] cursor-pointer transition-colors ${
                        isSelected ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 ${badge?.cls ?? 'bg-slate-100 text-slate-500'}`}>
                        <i className={`${badge?.icon ?? 'ri-store-3-line'} text-sm`} />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-[#4f46e5]' : 'text-[#1e293b]'}`}>
                          {wh.name}
                        </p>
                        <p className="text-[10px] text-[#94a3b8]">{badge?.label ?? wh.type}</p>
                      </div>
                      {isSelected && <i className="ri-check-line text-[#4f46e5] shrink-0" />}
                    </button>
                  </li>
                );
              })
            )}
          </ul>

          <div className="px-3 py-1.5 border-t border-[#e2e8f0] text-[10px] text-[#94a3b8]">
            {filtered.length} of {warehouses.length} warehouses
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ItemForm types ───────────────────────────────────────────────────────────
export interface ItemFormData {
  name: string;
  code: string;
  barcode: string;
  categoryId: string;
  categoryName: string;
  brand: string;
  hsnCode: string;
  hsnDescription?: string | null;
  hsnVerified?: boolean;
  taxRate: number;
  unitId: string;
  unitName: string;
  purchaseRate: number;
  saleRate: number;
  minStockLevel: number;
  articleNo: string;
  isActive: boolean;
  warehouseId: string;
  openingStock: number;
  // ── Manufacturing ──
  itemType: '' | 'RAW_MATERIAL' | 'SEMI_FINISHED' | 'FINISHED_GOOD' | 'CONSUMABLE' | 'PACKAGING';
  procurementType?: ProcurementType | '';
  productionUnit?: string | null;
  standardCost?: number;
  isBatchTracked?: boolean;
  isSerialTracked?: boolean;
  hasExpiry?: boolean;
  shelfLifeDays?: number | null;
  bomId?: string | null;
  bomVersion?: string | null;
  reorderPoint?: number;
  reorderQty?: number;
  leadTimeDays?: number;
  preferredSupplierId?: string | null;
  // ── Sub-contracting (procurement SUBCONTRACT) ──
  preferredSubcontractorId?: string | null;
  jobWorkRate?: number;
  /** What one stock unit fetches as scrap (₹); 0 = zero-value scrap */
  scrapRate?: number;
  itemGroup?: string | null;
  drawingNumber?: string | null;
  specifications?: string | null;
  requiresIncomingQC?: boolean;
  requiresFinalQC?: boolean;
  // ── Variant Settings ──
  isParent?: boolean;
  hasVariants?: boolean;
  serialPrefix?: string | null;
  defaultWarrantyMonths?: number | null;
}

interface ItemFormProps {
  open: boolean;
  initialData?: Partial<ItemFormData>;
  isEditing: boolean;
  itemId?: string;
  onClose: () => void;
  onSave: (data: ItemFormData) => Promise<void>;
}

const GST_RATES = [0, 5, 12, 18, 28];

const ITEM_TYPE_OPTIONS: { value: ItemFormData['itemType']; label: string }[] = [
  { value: 'RAW_MATERIAL', label: 'Raw Material' },
  { value: 'SEMI_FINISHED', label: 'Semi-Finished' },
  { value: 'FINISHED_GOOD', label: 'Finished Good' },
  { value: 'CONSUMABLE', label: 'Consumable' },
  { value: 'PACKAGING', label: 'Packaging Material' },
];

type ProcurementType = 'MAKE' | 'BUY' | 'SUBCONTRACT';

const PROCUREMENT_TYPE_OPTIONS: { value: ProcurementType; label: string; hint: string }[] = [
  { value: 'MAKE', label: 'Make', hint: 'Produced in-house (BOM + Production Order)' },
  { value: 'BUY', label: 'Buy', hint: 'Purchased from a supplier' },
  { value: 'SUBCONTRACT', label: 'Subcontract', hint: 'Processed by a job worker' },
];

// Item type decides the default procurement type and which ones are allowed
const PROCUREMENT_RULES: Record<string, { default: ProcurementType; allowed: ProcurementType[] }> = {
  RAW_MATERIAL: { default: 'BUY', allowed: ['BUY', 'SUBCONTRACT'] },
  SEMI_FINISHED: { default: 'MAKE', allowed: ['MAKE', 'BUY', 'SUBCONTRACT'] },
  FINISHED_GOOD: { default: 'MAKE', allowed: ['MAKE', 'BUY', 'SUBCONTRACT'] },
  CONSUMABLE: { default: 'BUY', allowed: ['BUY'] },
  PACKAGING: { default: 'BUY', allowed: ['BUY'] },
};

function procurementRuleFor(itemType: string) {
  return PROCUREMENT_RULES[itemType] || null;
}

type CostFields = Pick<ItemFormData, 'itemType' | 'procurementType' | 'purchaseRate' | 'saleRate' | 'standardCost'>;

/**
 * Cost the item is priced against: the purchase rate for a bought item, the standard cost
 * (from its BOM) for one made in-house or by a sub-contractor.
 */
function costBasisOf(f: CostFields) {
  const isStandard = f.procurementType === 'MAKE' || f.procurementType === 'SUBCONTRACT';
  return {
    isStandard,
    cost: Number(isStandard ? f.standardCost : f.purchaseRate) || 0,
    label: isStandard ? 'standard cost' : 'purchase rate',
  };
}

/**
 * QC defaults suggested by the procurement type: bought-in and sub-contracted goods are checked
 * when they arrive, made and sub-contracted goods before dispatch. Applied when the procurement
 * type is chosen; the user can still switch either off.
 */
function qcSuggestion(procurementType: ProcurementType | '' | undefined) {
  return {
    requiresIncomingQC: procurementType === 'BUY' || procurementType === 'SUBCONTRACT',
    requiresFinalQC: procurementType === 'MAKE' || procurementType === 'SUBCONTRACT',
  };
}

/** Fields a SUBCONTRACT item cannot be saved without (shown in the Manufacturing tab). */
const SUBCONTRACT_FIELDS = ['preferredSubcontractorId', 'jobWorkRate', 'leadTimeDays', 'drawingNumber', 'specifications'] as const;

function subcontractErrors(f: ItemFormData): Record<string, string> {
  const e: Record<string, string> = {};
  if (f.procurementType !== 'SUBCONTRACT') return e;
  if (!f.preferredSubcontractorId) e.preferredSubcontractorId = 'Select the sub-contractor who makes this item';
  if (!(Number(f.jobWorkRate) > 0)) e.jobWorkRate = 'Job work charge per unit is required';
  if (!(Number(f.leadTimeDays) > 0)) e.leadTimeDays = 'Sub-contract lead time is required';
  if (!String(f.drawingNumber ?? '').trim()) e.drawingNumber = 'Drawing number is required for a sub-contracted item';
  if (!String(f.specifications ?? '').trim()) e.specifications = 'Specifications are required for a sub-contracted item';
  return e;
}

/** Sale rate is mandatory for a finished good and should not be below its cost. */
function saleRateError(f: CostFields): string | null {
  if (f.itemType === 'FINISHED_GOOD' && !(Number(f.saleRate) > 0)) return 'Sale rate is required for a finished good';
  const basis = costBasisOf(f);
  if (Number(f.saleRate) > 0 && basis.cost > 0 && Number(f.saleRate) < basis.cost) {
    return `Sale rate is lower than the ${basis.label}`;
  }
  return null;
}

const defaultForm: ItemFormData = {
  name: '',
  code: '',
  barcode: '',
  categoryId: '',
  categoryName: '',
  brand: '',
  hsnCode: '',
  hsnDescription: null,
  hsnVerified: false,
  taxRate: 18,
  unitId: '',
  unitName: '',
  purchaseRate: 0,
  saleRate: 0,
  minStockLevel: 5,
  articleNo: '',
  isActive: true,
  warehouseId: '',
  openingStock: 0,
  itemType: '',
  procurementType: '',
  productionUnit: null,
  standardCost: 0,
  isBatchTracked: false,
  isSerialTracked: false,
  hasExpiry: false,
  shelfLifeDays: null,
  bomId: null,
  bomVersion: null,
  reorderPoint: 0,
  reorderQty: 0,
  leadTimeDays: 0,
  preferredSupplierId: null,
  preferredSubcontractorId: null,
  jobWorkRate: 0,
  scrapRate: 0,
  itemGroup: null,
  drawingNumber: null,
  specifications: null,
  requiresIncomingQC: false,
  requiresFinalQC: false,
  isParent: false,
  hasVariants: false,
  serialPrefix: '',
  defaultWarrantyMonths: 12,
};

const Field = ({ label, children, required, error }: { label: React.ReactNode; children: React.ReactNode; required?: boolean; error?: string }) => (
  <div className="space-y-1.5">
    <div className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
      {label} {required && <span className="text-red-500">*</span>}
    </div>
    {children}
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">
    <h4 className="text-sm font-semibold text-[#1e293b] border-b border-[#e2e8f0] pb-1">{title}</h4>
    <div className="space-y-3">{children}</div>
  </div>
);

const Toggle = ({
  label,
  checked,
  onChange,
  helper,
  icon,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  helper?: string;
  icon?: string;
}) => (
  <div
    onClick={onChange}
    className={`flex items-center justify-between p-3.5 rounded-xl border transition-all cursor-pointer select-none ${
      checked
        ? 'bg-indigo-50/50 border-indigo-200'
        : 'bg-[#f8fafc] border-[#e2e8f0] hover:border-[#cbd5e1]'
    }`}
  >
    <div className="flex items-start gap-3 min-w-0 pr-3">
      {icon && (
        <div
          className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${
            checked ? 'bg-indigo-100 text-[#4f46e5]' : 'bg-slate-100 text-[#64748b]'
          }`}
        >
          <i className={`${icon} text-base`} />
        </div>
      )}
      <div className="min-w-0">
        <p className={`text-sm font-semibold ${checked ? 'text-[#1e293b]' : 'text-[#334155]'}`}>
          {label}
        </p>
        {helper && <p className="text-xs text-[#64748b] mt-0.5">{helper}</p>}
      </div>
    </div>

    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 ${
        checked ? 'bg-[#4f46e5]' : 'bg-[#cbd5e1]'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

// ─── ItemForm ─────────────────────────────────────────────────────────────────
export default function ItemForm({
  open,
  initialData,
  isEditing,
  itemId,
  onClose,
  onSave,
}: ItemFormProps) {
  const formRef = useRef<HTMLDivElement>(null);
  const barcodeRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const barcodeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = useNavigate();
  const { error: toastError } = useToast();
  const { selectedWarehouseId, setSelectedWarehouse } = useWarehouseStore();
  const { hasControl } = useAuth();
  const canViewAll = hasControl('viewAllWarehouses');

  // ── Tab State ──
  const [activeTab, setActiveTab] = useState<'basic' | 'manufacturing' | 'tracking'>('basic');

  const isSerialInitial = Boolean((initialData as any)?.is_serial_tracked ?? initialData?.isSerialTracked);
  const initialRawPrefix = (initialData as any)?.serial_prefix ?? initialData?.serialPrefix ?? '';
  const initialResolvedPrefix = initialRawPrefix || (isSerialInitial ? deriveSerialPrefix(initialData?.name, initialData?.code, (initialData as any)?.brand ?? initialData?.brand) : '');

  const [isManualPrefix, setIsManualPrefix] = useState<boolean>(Boolean(initialRawPrefix));

  // ── Form State ──
  const [form, setForm] = useState<ItemFormData>(() => ({
    ...defaultForm,
    ...initialData,
    serialPrefix: initialResolvedPrefix,
    defaultWarrantyMonths: (initialData as any)?.default_warranty_months ?? initialData?.defaultWarrantyMonths ?? 12,
    warehouseId: isEditing
      ? (initialData?.warehouseId ?? '')
      : (selectedWarehouseId ?? ''),
  }));

  // ── Suppliers (for MRP's preferred-supplier picker) ──
  const [suppliers, setSuppliers] = useState<PartyResponse[]>([]);
  useEffect(() => {
    getSuppliers()
      .then((res) => setSuppliers(res.data || []))
      .catch(() => {});
  }, []);

  // ── Sub-contractors (for a SUBCONTRACT item) ──
  const [subContractors, setSubContractors] = useState<{ id: string; name: string; is_active?: boolean }[]>([]);
  useEffect(() => {
    jobWorkService.getSubContractors()
      .then((rows) => setSubContractors(rows.filter((r: { is_active?: boolean }) => r.is_active !== false)))
      .catch(() => {});
  }, []);

  const [hasVariants, setHasVariants] = useState<boolean>(
    initialData?.isParent || initialData?.hasVariants || false
  );

  useEffect(() => {
    if (!open) return;
    const isSerial = Boolean((initialData as any)?.is_serial_tracked ?? initialData?.isSerialTracked);
    const rawPrefix = (initialData as any)?.serial_prefix ?? initialData?.serialPrefix ?? '';
    const resolvedPrefix = rawPrefix || (isSerial ? deriveSerialPrefix(initialData?.name, initialData?.code, initialData?.brand) : '');
    setIsManualPrefix(Boolean(rawPrefix));

    setForm({
      ...defaultForm,
      ...initialData,
      serialPrefix: resolvedPrefix,
      defaultWarrantyMonths: (initialData as any)?.default_warranty_months ?? initialData?.defaultWarrantyMonths ?? 12,
      warehouseId: isEditing
        ? (initialData?.warehouseId ?? '')
        : (selectedWarehouseId ?? ''),
    });
    setHasVariants(initialData?.isParent || initialData?.hasVariants || false);
    setAutoCode(!isEditing);
    setErrors({});
    setApiError('');
  }, [open, initialData, isEditing, selectedWarehouseId]);

  // Forms opened with an item type but no procurement type (older items, prefilled forms) get its default
  useEffect(() => {
    if (!open || !form.itemType || form.procurementType) return;
    const rule = procurementRuleFor(form.itemType);
    if (!rule) return;
    // Existing items keep their saved QC flags; a new item starts from the suggestion
    setForm((p) => (p.procurementType ? p : { ...p, procurementType: rule.default, ...(isEditing ? {} : qcSuggestion(rule.default)) }));
  }, [open, form.itemType, form.procurementType, isEditing]);

  // Dynamically update serialPrefix from name/brand when serial tracking is enabled unless manually edited
  useEffect(() => {
    if (form.isSerialTracked && !isManualPrefix) {
      const derived = deriveSerialPrefix(form.name, form.code, form.brand);
      if (derived && derived !== 'SN') {
        setForm((p) => (p.serialPrefix !== derived ? { ...p, serialPrefix: derived } : p));
      } else if (!form.serialPrefix && derived) {
        setForm((p) => ({ ...p, serialPrefix: derived }));
      }
    }
  }, [form.name, form.brand, form.code, form.isSerialTracked, isManualPrefix]);

  const [liveSerialPreview, setLiveSerialPreview] = useState<{ first: string; second: string } | null>(null);

  const effectivePrefix = useMemo(() => {
    return (
      form.serialPrefix ||
      deriveSerialPrefix(form.name, form.code, form.brand)
    )
      .replace(/[^A-Za-z0-9]/g, '')
      .slice(0, 6)
      .toUpperCase();
  }, [form.serialPrefix, form.name, form.code, form.brand]);

  useEffect(() => {
    if (!open || !form.isSerialTracked || !effectivePrefix) {
      setLiveSerialPreview(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await serialApi.generateSerialNumber(undefined, effectivePrefix);
        if (res?.serialNumber) {
          const first = res.serialNumber;
          const match = first.match(/^(SN-.*?-)(\d+)$/);
          let second = '';
          if (match) {
            const num = parseInt(match[2], 10);
            second = `${match[1]}${String(num + 1).padStart(match[2].length, '0')}`;
          } else {
            second = `${first}-0002`;
          }
          setLiveSerialPreview({ first, second });
        }
      } catch (_) {
        // fallback
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [open, form.isSerialTracked, effectivePrefix]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [verifyingHsn, setVerifyingHsn] = useState(false);
  const [hsnVerifyError, setHsnVerifyError] = useState<string | null>(null);
  const [authenticatingHsn, setAuthenticatingHsn] = useState(false);
  const [hsnAuthNote, setHsnAuthNote] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [autoCode, setAutoCode] = useState(!isEditing);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [barcodeMsg, setBarcodeMsg] = useState<{
    type: 'success' | 'info' | 'error';
    text: string;
  } | null>(null);

  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [units, setUnits] = useState<SelectOption[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const [warehousesLoading, setWarehousesLoading] = useState(false);

  // ── Fetch categories + units ──
  useEffect(() => {
    let mounted = true;
    setDropdownsLoading(true);
    void (async () => {
      try {
        const [catRes, unitRes] = await Promise.all([
          fetch(`${BASE_URL}/api/v1/categories/all`, { headers: authHeaders() }),
          fetch(`${BASE_URL}/api/v1/unit/all`, { headers: authHeaders() }),
        ]);
        const [catData, unitData] = await Promise.all([
          catRes.json(),
          unitRes.json(),
        ]);
        if (!mounted) return;
        setCategories(catData.data ?? []);
        setUnits(
          (unitData.data ?? []).map((u: any) => ({
            id: u.id,
            name: u.shortName || u.short_name || u.name,
            isActive: u.isActive !== false,
            allowDecimal: u.allowDecimal !== false,
          })),
        );
      } catch {
        /* silent */
      } finally {
        if (mounted) setDropdownsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Opening stock while being typed ("2." must survive)
  const [openingStockText, setOpeningStockText] = useState<string | null>(null);

  // ── Stock unit lock: an item with stock or transactions keeps its unit ──
  const [unitLocked, setUnitLocked] = useState(false);
  const editingItemId = isEditing ? (initialData as any)?.id : null;
  useEffect(() => {
    setUnitLocked(false);
    if (!open || !editingItemId) return;
    let mounted = true;
    fetch(`${BASE_URL}/api/v1/item/${editingItemId}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        if (mounted) setUnitLocked(Boolean(d?.data?.unit_locked));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [open, editingItemId]);

  // Inactive units are not offered, except the one the item already uses
  const unitOptions = useMemo(
    () => units.filter((u) => u.isActive !== false || u.id === form.unitId),
    [units, form.unitId],
  );

  // ── Fetch warehouses ──
  useEffect(() => {
    let mounted = true;
    setWarehousesLoading(true);
    void (async () => {
      try {
        const res = await getWarehousesForUser(canViewAll);
        if (!mounted) return;
        const rows: Warehouse[] = (res.data ?? []).map((w: WarehouseResponse) => ({
          id: w.id,
          name: w.name,
          type: w.type,
          isActive: w.is_active,
        }));
        setWarehouses(rows.filter((w) => w.isActive));
      } catch {
        if (mounted) setWarehouses([]);
      } finally {
        if (mounted) setWarehousesLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [canViewAll]);

  // ── Reset on open ──
  useEffect(() => {
    if (!open) return;
    const warehouseId = isEditing
      ? (initialData?.warehouseId ?? '')
      : (selectedWarehouseId ?? '');

    setForm({ ...defaultForm, ...initialData, warehouseId });
    setHasVariants(initialData?.isParent || initialData?.hasVariants || false);
    setAutoCode(!isEditing);
    setErrors({});
    setApiError('');
    setBarcodeMsg(null);
    setDuplicateWarning(null);
    setActiveTab('basic');
    // Start on Item Name (a scanner user clicks Barcode). Never take focus from a field the user is
    // already typing in — the drawer can still be animating when this runs.
    setTimeout(() => {
      const typing = document.activeElement;
      if (typing instanceof HTMLInputElement || typing instanceof HTMLTextAreaElement) return;
      nameRef.current?.focus();
    }, 80);
  }, [open, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback(
    (field: keyof ItemFormData, value: string | number | boolean | null) => {
      setForm((p) => ({ ...p, [field]: value }));
      setApiError('');
      setErrors((e) => {
        if (!e[field]) return e;
        const n = { ...e };
        delete n[field];
        return n;
      });
    },
    [],
  );

  // A new item type resets the procurement type to its default when the current one is not allowed
  const handleItemTypeChange = useCallback((itemType: ItemFormData['itemType']) => {
    const rule = procurementRuleFor(itemType);
    setForm((p) => {
      const current = p.procurementType as ProcurementType | '';
      const keep = !!current && !!rule && rule.allowed.includes(current);
      const procurementType = keep ? current : (rule?.default ?? '');
      return {
        ...p,
        itemType,
        procurementType,
        ...(procurementType && procurementType !== current ? qcSuggestion(procurementType) : {}),
      };
    });
    setApiError('');
    setErrors((e) => {
      if (!e.itemType && !e.procurementType) return e;
      const n = { ...e };
      delete n.itemType;
      delete n.procurementType;
      return n;
    });
  }, []);

  const handleProcurementTypeChange = useCallback((procurementType: ProcurementType) => {
    setForm((p) => (p.procurementType === procurementType ? p : { ...p, procurementType, ...qcSuggestion(procurementType) }));
    setApiError('');
    setErrors((e) => {
      if (!e.procurementType) return e;
      const n = { ...e };
      delete n.procurementType;
      return n;
    });
  }, []);

  const handleHsnCodeChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    setForm((p) => ({ ...p, hsnCode: digits, hsnVerified: false, hsnDescription: null }));
    setHsnVerifyError(null);
    setApiError('');
    setErrors((e) => {
      if (!e.hsnCode) return e;
      const n = { ...e };
      delete n.hsnCode;
      return n;
    });
  }, []);

  const handleVerifyHsn = useCallback(async () => {
    const clean = form.hsnCode.trim();
    if (![4, 6, 8].includes(clean.length)) {
      setHsnVerifyError('HSN code must be 4, 6 or 8 digits');
      return;
    }
    setVerifyingHsn(true);
    setHsnVerifyError(null);
    setHsnAuthNote(null);
    try {
      const res = await complianceApi.verifyHsn(clean);
      if (res?.success && res.data) {
        setForm((p) => ({
          ...p,
          hsnVerified: true,
          hsnDescription: res.data!.description ?? null,
        }));
        clearError('hsnCode');
      } else {
        setForm((p) => ({ ...p, hsnVerified: false, hsnDescription: null }));
        setHsnVerifyError(res?.message || 'HSN code could not be verified');
      }
    } catch (err: any) {
      setForm((p) => ({ ...p, hsnVerified: false, hsnDescription: null }));
      setHsnVerifyError(err?.message || 'HSN code could not be verified');
    } finally {
      setVerifyingHsn(false);
    }
  }, [form.hsnCode]);

  const isHsnAuthError = useCallback((message: string) => {
    const m = message.toLowerCase();
    return m.includes('token') || m.includes('authenticat') || m.includes('credential');
  }, []);

  const handleAuthenticateHsn = useCallback(async () => {
    setAuthenticatingHsn(true);
    setHsnAuthNote(null);
    try {
      const res = await complianceApi.testEWayBillAuth({});
      if (res?.success) {
        setHsnAuthNote(res.message || 'Authenticated with Whitebooks ✓ — try Verify again.');
        setHsnVerifyError(null);
      } else {
        setHsnAuthNote(res?.message || 'Authentication failed');
      }
    } catch (err: any) {
      setHsnAuthNote(err?.message || 'Authentication failed');
    } finally {
      setAuthenticatingHsn(false);
    }
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors((e) => {
      if (!e[field]) return e;
      const n = { ...e };
      delete n[field];
      return n;
    });
  }, []);

  const onCategoryChange = useCallback(
    (catId: string) => {
      setForm((p) => {
        const cat = categories.find((c) => c.id === catId);
        const next = { ...p, categoryId: catId, categoryName: cat?.name ?? '' };
        // A new item takes the category's default HSN / GST; an HSN already typed is kept
        if (!isEditing && cat) {
          if (cat.defaultHsnCode && !p.hsnCode.trim()) {
            Object.assign(next, { hsnCode: cat.defaultHsnCode, hsnVerified: false, hsnDescription: null });
          }
          if (cat.defaultGstRate !== null && cat.defaultGstRate !== undefined && GST_RATES.includes(cat.defaultGstRate)) {
            next.taxRate = cat.defaultGstRate;
          }
        }
        return next;
      });
      clearError('categoryId');
    },
    [categories, clearError, isEditing],
  );

  const onUnitChange = useCallback(
    (unitId: string) => {
      setForm((p) => {
        const unit = units.find((u) => u.id === unitId);
        return { ...p, unitId, unitName: unit?.name ?? '' };
      });
      clearError('unitId');
    },
    [units, clearError],
  );

  const onWarehouseChange = useCallback(
    (id: string, name: string) => {
      setForm((p) => ({ ...p, warehouseId: id }));
      setSelectedWarehouse(id, name);
      clearError('warehouseId');
    },
    [setSelectedWarehouse, clearError],
  );

  // ── Batch & Serial mutual exclusivity ──
  const toggleBatch = () => {
    const newVal = !form.isBatchTracked;
    if (newVal && form.isSerialTracked) {
      toastError('Cannot enable both Batch and Serial tracking');
      return;
    }
    update('isBatchTracked', newVal);
    if (!newVal) {
      update('hasExpiry', false);
      update('shelfLifeDays', null);
    }
  };

  const toggleSerial = () => {
    if (!form.isSerialTracked && form.isBatchTracked) {
      toastError('Cannot enable both Batch and Serial tracking');
      return;
    }
    const nextVal = !form.isSerialTracked;
    update('isSerialTracked', nextVal);
    if (nextVal) {
      if (!form.serialPrefix || !isManualPrefix) {
        const autoPrefix = deriveSerialPrefix(form.name, form.code, form.brand);
        update('serialPrefix', autoPrefix);
      }
      if (!form.defaultWarrantyMonths) {
        update('defaultWarrantyMonths', 12);
      }
    }
  };

  // ── Barcode search ──
  const handleBarcodeChange = useCallback(
    async (val: string) => {
      setForm((p) => ({ ...p, barcode: val }));
      setDuplicateWarning(null);
      setApiError('');
      if (!val.trim() || val.trim().length < 3) return;

      if (barcodeDebounceRef.current) clearTimeout(barcodeDebounceRef.current);
      barcodeDebounceRef.current = setTimeout(async () => {
        try {
          const res = await filterItems({ search: val.trim() });
          const found = (res.data ?? []).find(
            (r: any) => r.barcode === val.trim() || r.code === val.trim(),
          );
          if (!found) return;
          const mapped = mapApiToItem(found);
          setForm((prev) => ({ ...prev, ...mapped, barcode: val.trim() }));
          setAutoCode(false);
          const isSelf = isEditing && found.code === initialData?.code;
          if (isSelf) {
            setBarcodeMsg({
              type: 'success',
              text: `Item found: ${mapped.name}`,
            });
            setTimeout(() => setBarcodeMsg(null), 3000);
          } else {
            setDuplicateWarning(
              `Barcode already used by: "${found.name}" (Code: ${found.code})`,
            );
          }
        } catch {
          /* silent */
        }
      }, 400);
    },
    [isEditing, initialData?.code],
  );

  const handleBarcodeKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const val = (e.target as HTMLInputElement).value.trim();
      if (!val) return;
      void handleBarcodeChange(val);
    }
  }, [handleBarcodeChange]);

  // ── Name duplicate check ──
  const handleNameChange = useCallback(
    async (val: string) => {
      update('name', val);
      setDuplicateWarning(null);
      setApiError('');
      if (!val.trim() || val.trim().length < 2) return;

      if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
      nameDebounceRef.current = setTimeout(async () => {
        try {
          const currentWarehouseId = form.warehouseId || undefined;
          const res = await filterItems({
            search: val.trim(),
            warehouseId: currentWarehouseId,
          });

          const found = (res.data ?? []).find(
            (r: any) => r.name.toLowerCase() === val.trim().toLowerCase(),
          );
          if (!found) return;

          const isSelf = isEditing && found.code === initialData?.code;
          if (!isSelf) {
            setDuplicateWarning(
              `An item named "${found.name}" already exists (Code: ${found.code})`,
            );
          }
        } catch {
          /* silent */
        }
      }, 400);
    },
    [isEditing, initialData?.code, update, form.warehouseId],
  );

  useEffect(
    () => () => {
      if (barcodeDebounceRef.current) clearTimeout(barcodeDebounceRef.current);
      if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // ── Validation ──
  const validate = (currentForm: ItemFormData) => {
    const e: Record<string, string> = {};
    // Basic Info validation (always required)
    if (!currentForm.name.trim()) e.name = 'Item name is required';
    if (!autoCode && !currentForm.code.trim()) e.code = 'Item code is required';
    if (!currentForm.hsnCode.trim()) {
      e.hsnCode = 'HSN code is required';
    } else if (![4, 6, 8].includes(currentForm.hsnCode.trim().length)) {
      e.hsnCode = 'HSN code must be 4, 6 or 8 digits';
    }
    if (!currentForm.warehouseId) e.warehouseId = 'Please select a warehouse';
    const saleError = saleRateError(currentForm);
    if (saleError) e.saleRate = saleError;

    // Mandatory Manufacturing & Tracking validation
    if (!currentForm.itemType) {
      e.itemType = 'Item type classification is mandatory';
    }
    const procurementRule = procurementRuleFor(currentForm.itemType);
    if (!currentForm.procurementType) {
      e.procurementType = 'Procurement type is mandatory';
    } else if (procurementRule && !procurementRule.allowed.includes(currentForm.procurementType)) {
      e.procurementType = `Allowed for this item type: ${procurementRule.allowed.join(', ')}`;
    }
    if (currentForm.hasExpiry && (!currentForm.shelfLifeDays || Number(currentForm.shelfLifeDays) <= 0)) {
      e.shelfLifeDays = 'Shelf life days is mandatory when expiry control is enabled';
    }
    Object.assign(e, subcontractErrors(currentForm));

    setErrors(e);

    if (Object.keys(e).length > 0) {
      if (e.name || e.code || e.hsnCode || e.warehouseId || e.saleRate || e.itemType || e.procurementType || e.standardCost) {
        setActiveTab('basic');
      } else if (SUBCONTRACT_FIELDS.some((k) => e[k])) {
        setActiveTab('manufacturing');
      } else if (e.shelfLifeDays) {
        setActiveTab('tracking');
      }
    }

    return Object.keys(e).length === 0;
  };

  const handleNextTab = () => {
    if (activeTab === 'basic') {
      const e: Record<string, string> = {};
      if (!form.name.trim()) e.name = 'Item name is required';
      if (!autoCode && !form.code.trim()) e.code = 'Item code is required';
      if (!form.hsnCode.trim()) {
        e.hsnCode = 'HSN code is required';
      } else if (![4, 6, 8].includes(form.hsnCode.trim().length)) {
        e.hsnCode = 'HSN code must be 4, 6 or 8 digits';
      }
      if (!form.warehouseId) e.warehouseId = 'Please select a warehouse';
      if (!form.itemType) e.itemType = 'Item type classification is mandatory';
      if (!form.procurementType) e.procurementType = 'Procurement type is mandatory';
      const saleError = saleRateError(form);
      if (saleError) e.saleRate = saleError;

      if (Object.keys(e).length > 0) {
        setErrors((prev) => ({ ...prev, ...e }));
        return;
      }
      setActiveTab('manufacturing');
    } else if (activeTab === 'manufacturing') {
      const e = subcontractErrors(form);
      if (Object.keys(e).length > 0) {
        setErrors((prev) => ({ ...prev, ...e }));
        return;
      }
      setActiveTab('tracking');
    }
  };

  const isSavingRef = useRef(false);

  const handleSave = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    setApiError('');

    const snapshot = {
      ...form,
      isParent: hasVariants,
      hasVariants: hasVariants,
    };

    if (!validate(snapshot)) {
      isSavingRef.current = false;
      setIsSaving(false);
      return;
    }

    try {
      await onSave(snapshot);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to save item');
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  if (!open) return null;

  // Margin against the purchase rate (Buy) or the standard cost (Make / Subcontract)
  const costBasis = costBasisOf(form);
  const margin =
    costBasis.cost > 0 && form.saleRate > 0
      ? (((form.saleRate - costBasis.cost) / costBasis.cost) * 100).toFixed(1)
      : null;
  const marginVal = margin !== null ? parseFloat(margin) : null;

  const isSubcontract = form.procurementType === 'SUBCONTRACT';
  // Only made items (semi-finished / finished goods) have a BOM, whatever their procurement type
  const showBomSection = form.itemType === 'SEMI_FINISHED' || form.itemType === 'FINISHED_GOOD';
  const leadTimeLabel =
    form.itemType === 'RAW_MATERIAL'
      ? 'Supplier Lead Time'
      : form.itemType === 'FINISHED_GOOD'
        ? 'Production Lead Time'
        : 'Lead Time';

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div ref={formRef} className="relative ml-auto h-full w-full max-w-lg bg-white shadow-2xl flex flex-col">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] shrink-0">
          <div>
            <h3 className="text-base font-semibold text-[#1e293b]">
              {isEditing ? 'Edit Item' : 'Add New Item'}
            </h3>
            <p className="text-xs text-[#64748b] mt-0.5">
              Scan barcode or fill details manually
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* ── Tabs Navigation ── */}
        <div className="flex items-center gap-1 px-6 pt-3 pb-0 border-b border-[#e2e8f0] shrink-0">
          {[
            { key: 'basic' as const, label: 'Basic Info' },
            { key: 'manufacturing' as const, label: 'Manufacturing' },
            { key: 'tracking' as const, label: 'Tracking' },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-[#4f46e5] text-[#4f46e5]'
                  : 'border-transparent text-[#64748b] hover:text-[#1e293b]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Error banner ── */}
        {apiError && (
          <div className="mx-6 mt-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm shrink-0">
            <i className="ri-error-warning-line text-base shrink-0 mt-0.5" />
            <p>{apiError}</p>
          </div>
        )}

        {/* ── Duplicate warning ── */}
        {duplicateWarning && (
          <div className="mx-6 mt-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm shrink-0">
            <i className="ri-error-warning-line text-base shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Duplicate item detected</p>
              <p>{duplicateWarning}</p>
              <p className="mt-1 text-xs text-amber-600">
                Details have been auto-filled. Clear the name/barcode to create a new item.
              </p>
            </div>
          </div>
        )}

        {/* ── Form Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* ═══ TAB 1 — BASIC INFO ═══ */}
          <div className={activeTab === 'basic' ? 'block space-y-5' : 'hidden'}>
            {/* ── WAREHOUSE ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide flex items-center gap-2">
                Warehouse <span className="text-red-500">*</span>
                {warehousesLoading && (
                  <i className="ri-loader-4-line animate-spin text-[#94a3b8]" />
                )}
              </label>
              <WarehouseSelect
                value={form.warehouseId}
                onChange={onWarehouseChange}
                warehouses={warehouses}
                loading={warehousesLoading}
                error={!!errors.warehouseId}
              />
              {errors.warehouseId && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" /> {errors.warehouseId}
                </p>
              )}
            </div>

            <div className="border-t border-[#f1f5f9]" />

            {/* ── BARCODE ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide flex items-center gap-2">
                Barcode / SKU
                <span className="flex items-center gap-1 text-[10px] text-green-600 font-medium normal-case">
                  <span className="relative flex w-1.5 h-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-green-500" />
                  </span>
                  Scan or type
                </span>
              </label>
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <input
                    ref={barcodeRef}
                    type="text"
                    value={form.barcode}
                    onChange={(e) => void handleBarcodeChange(e.target.value)}
                    onKeyDown={handleBarcodeKeyDown}
                    placeholder="Scan barcode or type manually — press Enter"
                    data-nav-index={0}
                    className="w-full h-10 px-3 pl-9 rounded-lg border border-[#e2e8f0] text-sm font-mono text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                  />
                  <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] text-base" />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const generated = '890' + Date.now().toString().slice(-10);
                    update('barcode', generated);
                    setAutoCode(false);
                    setBarcodeMsg({ type: 'success', text: `Barcode generated: ${generated}` });
                    setTimeout(() => setBarcodeMsg(null), 3000);
                  }}
                  className="h-10 px-3 rounded-lg border border-[#e2e8f0] text-xs font-medium text-[#64748b] hover:bg-[#f1f5f9] cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-qr-code-line mr-1" /> Generate
                </button>
              </div>
              {barcodeMsg && (
                <p
                  className={`text-xs flex items-center gap-1 ${
                    barcodeMsg.type === 'success'
                      ? 'text-green-600'
                      : barcodeMsg.type === 'error'
                        ? 'text-red-500'
                        : 'text-amber-600'
                  }`}
                >
                  <i
                    className={
                      barcodeMsg.type === 'success'
                        ? 'ri-checkbox-circle-fill'
                        : barcodeMsg.type === 'error'
                          ? 'ri-error-warning-line'
                          : 'ri-information-line'
                    }
                  />
                  {barcodeMsg.text}
                </p>
              )}
            </div>

            {/* ── NAME ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Item Name <span className="text-red-500">*</span>
              </label>
              <input
                ref={nameRef}
                type="text"
                value={form.name}
                onChange={(e) => void handleNameChange(e.target.value)}
                placeholder='Laptop 15" Core i7'
                data-nav-index={1}
                className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${
                  errors.name ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                }`}
              />
              {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
            </div>

            {/* ── CODE ── */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Item Code <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const next = !autoCode;
                    setAutoCode(next);
                    if (next) update('code', '');
                  }}
                  className="text-[10px] text-[#4f46e5] font-medium cursor-pointer hover:text-indigo-700"
                >
                  {autoCode ? 'Switch to Manual' : 'Auto-generate'}
                </button>
              </div>
              <input
                type="text"
                value={autoCode ? 'Auto-generated' : form.code}
                onChange={(e) => update('code', e.target.value.toUpperCase())}
                readOnly={autoCode}
                placeholder="ITM-0301"
                data-nav-index={2}
                className={`w-full h-10 px-3 rounded-lg border text-sm font-mono focus:outline-none focus:ring-2 transition-colors ${
                  autoCode ? 'bg-[#f8fafc] text-slate-400 cursor-default' : 'bg-white text-[#1e293b]'
                } ${
                  errors.code ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                }`}
              />
              {errors.code && <p className="text-xs text-red-500">{errors.code}</p>}
            </div>

            {/* ── CATEGORY ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide flex items-center gap-2">
                Category
                {dropdownsLoading && (
                  <i className="ri-loader-4-line animate-spin text-[#94a3b8]" />
                )}
              </label>
              <SearchableSelect
                value={form.categoryId}
                onChange={onCategoryChange}
                options={categories}
                placeholder={`Select category… (${categories.length} available)`}
                loading={dropdownsLoading}
                navIndex={3}
              />
            </div>

            {/* ── ITEM TYPE + PROCUREMENT TYPE ── */}
            <Field label="Item Type" required error={errors.itemType}>
              <select
                value={form.itemType}
                onChange={(e) => handleItemTypeChange(e.target.value as ItemFormData['itemType'])}
                className={`w-full h-10 px-3 rounded-lg border text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 cursor-pointer ${
                  errors.itemType ? 'border-red-400 text-[#1e293b]' : 'border-[#e2e8f0] text-[#1e293b]'
                } ${!form.itemType ? 'text-[#94a3b8]' : ''}`}
              >
                <option value="" disabled>— Select item type —</option>
                {ITEM_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Procurement Type" required error={errors.procurementType}>
              <div className="flex flex-wrap gap-2">
                {PROCUREMENT_TYPE_OPTIONS.map((o) => {
                  const rule = procurementRuleFor(form.itemType);
                  const allowed = !rule || rule.allowed.includes(o.value);
                  const selected = form.procurementType === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      disabled={!form.itemType || !allowed}
                      onClick={() => handleProcurementTypeChange(o.value)}
                      title={allowed ? o.hint : 'Not allowed for this item type'}
                      className={`h-9 px-3 rounded-lg border text-xs font-semibold transition-all whitespace-nowrap ${
                        selected
                          ? 'bg-indigo-50 text-[#4f46e5] border-[#4f46e5] ring-2 ring-[#4f46e5]/20'
                          : 'border-[#e2e8f0] text-[#64748b] hover:border-[#4f46e5]/40 hover:text-[#4f46e5]'
                      } disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#e2e8f0] disabled:hover:text-[#64748b] cursor-pointer`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {!form.itemType ? (
                <p className="text-[11px] text-[#94a3b8] mt-1">Select an item type first</p>
              ) : (
                form.procurementType && (
                  <p className="text-[11px] text-[#94a3b8] mt-1">
                    {PROCUREMENT_TYPE_OPTIONS.find((o) => o.value === form.procurementType)?.hint}
                  </p>
                )
              )}
            </Field>

            {/* ── BRAND ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Brand
              </label>
              <input
                type="text"
                value={form.brand}
                onChange={(e) => update('brand', e.target.value)}
                placeholder="Brand name"
                data-nav-index={4}
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
              />
            </div>

            {/* ── HSN ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                HSN/SAC Code <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={form.hsnCode}
                  onChange={(e) => handleHsnCodeChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleVerifyHsn())}
                  placeholder="e.g. 853710"
                  maxLength={8}
                  inputMode="numeric"
                  data-nav-index={5}
                  className={`flex-1 h-10 px-3 rounded-lg border text-sm font-mono text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${
                    errors.hsnCode
                      ? 'border-red-400 focus:ring-red-200'
                      : form.hsnVerified
                        ? 'border-green-400 focus:ring-green-200'
                        : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                  }`}
                />
                <button
                  type="button"
                  onClick={handleVerifyHsn}
                  disabled={verifyingHsn || ![4, 6, 8].includes(form.hsnCode.trim().length)}
                  className="shrink-0 inline-flex items-center gap-1.5 h-10 px-3.5 text-xs font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {verifyingHsn ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-shield-check-line" />}
                  {form.hsnVerified ? 'Re-verify' : 'Verify'}
                </button>
              </div>

              {errors.hsnCode ? (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" /> {errors.hsnCode}
                </p>
              ) : hsnVerifyError ? (
                <div className="space-y-1.5">
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <i className="ri-error-warning-line" /> {hsnVerifyError}
                  </p>
                  {isHsnAuthError(hsnVerifyError) && (
                    <button
                      type="button"
                      onClick={handleAuthenticateHsn}
                      disabled={authenticatingHsn}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      {authenticatingHsn ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-shield-keyhole-line" />}
                      Authenticate with Whitebooks
                    </button>
                  )}
                  {hsnAuthNote && (
                    <p className="text-xs text-indigo-600 flex items-start gap-1">
                      <i className="ri-information-line mt-0.5 shrink-0" /> {hsnAuthNote}
                    </p>
                  )}
                </div>
              ) : form.hsnVerified && form.hsnDescription ? (
                <p className="text-xs text-green-600 flex items-start gap-1">
                  <i className="ri-checkbox-circle-line mt-0.5 shrink-0" />
                  <span>
                    Verified with NIC — <span className="font-medium">{form.hsnDescription}</span>.
                    Confirm this matches the selected Category.
                  </span>
                </p>
              ) : form.hsnVerified ? (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <i className="ri-checkbox-circle-line" /> Verified with NIC
                </p>
              ) : form.hsnCode.trim().length > 0 ? (
                <p className="text-xs text-amber-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" /> Not verified yet — click Verify before saving
                </p>
              ) : null}
            </div>

            {/* ── ARTICLE NO ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Article No.
              </label>
              <input
                type="text"
                value={form.articleNo}
                onChange={(e) => update('articleNo', e.target.value)}
                placeholder="ART-1001"
                data-nav-index={6}
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
              />
            </div>

            {/* ── COST + SALE RATE ── */}
            {/* Buy: purchase rate. Make / Subcontract: standard cost, taken from the BOM once one is linked */}
            <div className="grid grid-cols-2 gap-3">
              {costBasis.isStandard ? (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide flex items-center gap-1.5">
                    Standard Cost (₹)
                    {form.bomId && (
                      <span className="normal-case font-medium text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-[#4f46e5]">auto: BOM + routing</span>
                    )}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.standardCost || ''}
                    onChange={(e) => update('standardCost', parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0)}
                    readOnly={!!form.bomId}
                    placeholder="0"
                    data-nav-index={7}
                    title={form.bomId ? 'BOM material cost + routing labour / machine cost per unit' : 'Set automatically when an active BOM is linked'}
                    className={`w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 ${
                      form.bomId ? 'bg-[#f8fafc] cursor-default' : 'bg-white'
                    }`}
                  />
                  {!form.bomId && (
                    <p className="text-[11px] text-[#94a3b8]">Filled from the BOM once an active BOM is linked</p>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                    Purchase Rate (₹)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.purchaseRate || ''}
                    onChange={(e) => update('purchaseRate', parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    data-nav-index={7}
                    className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Sale Rate (₹) {form.itemType === 'FINISHED_GOOD' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.saleRate || ''}
                  onChange={(e) => update('saleRate', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  data-nav-index={8}
                  className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${
                    errors.saleRate ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                  }`}
                />
                {errors.saleRate && <p className="text-xs text-red-500">{errors.saleRate}</p>}
              </div>
            </div>

            {/* Margin indicator */}
            {marginVal !== null && (
              <div
                className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg ${
                  marginVal < 0 ? 'bg-red-50 text-red-600' : marginVal < 10 ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'
                }`}
              >
                <i className={marginVal < 0 ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} />
                Margin: {margin}%&nbsp;&nbsp;|&nbsp;&nbsp; Profit: ₹
                {Math.round((form.saleRate - costBasis.cost) * 100) / 100} per unit
                <span className="font-normal opacity-75">(on {costBasis.label})</span>
              </div>
            )}

            {/* ── GST RATE ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                GST Rate <span className="text-red-500">*</span>
              </label>
              <div
                tabIndex={0}
                data-nav-index={9}
                onKeyDown={(e) => {
                  const idx = GST_RATES.indexOf(form.taxRate);
                  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    update('taxRate', GST_RATES[Math.min(idx + 1, GST_RATES.length - 1)]);
                  }
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    update('taxRate', GST_RATES[Math.max(idx - 1, 0)]);
                  }
                }}
                className="flex gap-1.5 rounded-lg p-0.5 focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30"
              >
                {GST_RATES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => update('taxRate', r)}
                    className={`flex-1 h-10 rounded-lg text-xs font-bold border transition-colors cursor-pointer ${
                      form.taxRate === r ? 'bg-[#4f46e5] text-white border-[#4f46e5]' : 'bg-white text-[#64748b] border-[#e2e8f0] hover:border-[#4f46e5]'
                    }`}
                  >
                    {r}%
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#94a3b8]">Use ← → arrow keys to change when focused</p>
            </div>

            {/* ── UNIT ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide flex items-center gap-2">
                Unit <span className="text-red-500">*</span>
                {dropdownsLoading && <i className="ri-loader-4-line animate-spin text-[#94a3b8]" />}
              </label>
              <div title={unitLocked ? 'Transactions exist, unit cannot be changed' : undefined}>
                <SearchableSelect
                  value={form.unitId}
                  onChange={onUnitChange}
                  options={unitOptions}
                  placeholder={`Select unit… (${unitOptions.length} available)`}
                  loading={dropdownsLoading}
                  error={!!errors.unitId}
                  navIndex={10}
                  disabled={unitLocked}
                />
              </div>
              {unitLocked && (
                <p className="text-xs text-[#94a3b8] flex items-center gap-1">
                  <i className="ri-lock-line" /> Transactions exist, unit cannot be changed
                </p>
              )}
              {errors.unitId && <p className="text-xs text-red-500">{errors.unitId}</p>}
            </div>

            {/* ── OPENING STOCK ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Opening Stock
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={openingStockText ?? form.openingStock}
                onChange={(e) => {
                  // Decimals only when the unit allows them (Kg yes, Pcs no)
                  const val = e.target.value;
                  const wholeOnly = units.find((u) => u.id === form.unitId)?.allowDecimal === false;
                  if (!(wholeOnly ? /^\d*$/ : /^\d*\.?\d{0,6}$/).test(val)) return;
                  setOpeningStockText(val);
                  update('openingStock', val === '' || val === '.' ? 0 : parseFloat(val));
                }}
                onBlur={() => setOpeningStockText(null)}
                placeholder="0"
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
              />
            </div>

            {/* ── MIN STOCK ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Min Stock Level
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={form.minStockLevel}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  update('minStockLevel', val === '' ? 0 : parseInt(val, 10));
                }}
                data-nav-index={11}
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
              />
            </div>

            {/* ── ACTIVE TOGGLE ── */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                role="switch"
                aria-checked={form.isActive}
                onClick={() => update('isActive', !form.isActive)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 ${
                  form.isActive ? 'bg-[#4f46e5]' : 'bg-slate-200'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                    form.isActive ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <label
                onClick={() => update('isActive', !form.isActive)}
                className="text-sm font-medium text-[#475569] cursor-pointer select-none"
              >
                {form.isActive ? 'Active Item' : 'Inactive Item'}
              </label>
            </div>
          </div>

          {/* ═══ TAB 2 — MANUFACTURING ═══ */}
          <div className={activeTab === 'manufacturing' ? 'block space-y-5' : 'hidden'}>
            {isSubcontract && (
              <Section title="Sub-contracting">
                <Field label="Preferred Sub-contractor" required error={errors.preferredSubcontractorId}>
                  <select
                    value={form.preferredSubcontractorId ?? ''}
                    onChange={(e) => update('preferredSubcontractorId', e.target.value || null)}
                    className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.preferredSubcontractorId ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                  >
                    <option value="">Select sub-contractor</option>
                    {subContractors.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  {subContractors.length === 0 && (
                    <p className="text-xs text-[#94a3b8]">No sub-contractors yet — add one under Manufacturing → Job Work</p>
                  )}
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Job Work Charge / unit (₹)" required error={errors.jobWorkRate}>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={form.jobWorkRate || ''}
                      onChange={(e) => update('jobWorkRate', Math.max(0, parseFloat(e.target.value) || 0))}
                      placeholder="0.00"
                      className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${errors.jobWorkRate ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                    />
                  </Field>
                  <Field label="Lead Time (days)" required error={errors.leadTimeDays}>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.leadTimeDays || ''}
                      onChange={(e) => update('leadTimeDays', parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0)}
                      placeholder="Days"
                      className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.leadTimeDays ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                    />
                  </Field>
                </div>
              </Section>
            )}

            <Section title={isSubcontract ? 'Drawing & Specifications' : 'Classification'}>
              <Field label="Drawing Number" required={isSubcontract} error={errors.drawingNumber}>
                <input
                  type="text"
                  value={form.drawingNumber ?? ''}
                  onChange={(e) => update('drawingNumber', e.target.value || null)}
                  placeholder="e.g. DWG-001-A"
                  className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors font-mono ${errors.drawingNumber ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                />
              </Field>

              <Field label="Specifications" required={isSubcontract} error={errors.specifications}>
                <textarea
                  value={form.specifications ?? ''}
                  onChange={(e) => update('specifications', e.target.value || null)}
                  placeholder={isSubcontract ? 'What the sub-contractor must deliver: dimensions, grade, finish, tolerances...' : 'Technical specs, dimensions, grade, tolerances...'}
                  rows={3}
                  maxLength={500}
                  className={`w-full px-3 py-2 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors resize-none ${errors.specifications ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                />
              </Field>
            </Section>

            <Section title="Production Details">
              <div className="grid grid-cols-2 gap-3">
                {!isSubcontract && (
                  <Field label={leadTimeLabel}>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.leadTimeDays || ''}
                      onChange={(e) => update('leadTimeDays', parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0)}
                      className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </Field>
                )}
                <Field label="Reorder Point">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.reorderPoint || ''}
                    onChange={(e) => update('reorderPoint', parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0)}
                    className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                  <p className="text-xs text-[#94a3b8]">Alert at this stock level</p>
                </Field>
                <Field label="Reorder Qty">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.reorderQty || ''}
                    onChange={(e) => update('reorderQty', parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0)}
                    className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                  <p className="text-xs text-[#94a3b8]">Qty ordered on each reorder</p>
                </Field>
                {!isSubcontract && (
                  <Field label="Preferred Supplier">
                    <select
                      value={form.preferredSupplierId ?? ''}
                      onChange={(e) => update('preferredSupplierId', e.target.value || null)}
                      className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                    >
                      <option value="">Not set</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-[#94a3b8]">Used by MRP for purchase orders</p>
                  </Field>
                )}
                <Field label="Scrap Value / unit (₹)">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={form.scrapRate || ''}
                    onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }}
                    onChange={(e) => update('scrapRate', Math.max(0, parseFloat(e.target.value.replace(/-/g, '')) || 0))}
                    placeholder="0"
                    className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                  <p className="text-xs text-[#94a3b8]">What one {form.unitName || 'unit'} fetches as scrap; 0 = destroyed, no recovery</p>
                </Field>
              </div>
            </Section>

            {showBomSection && (
              <Section title={isSubcontract ? 'BOM Link (material to send) *' : 'BOM Link'}>
                {form.bomId ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-green-500 text-lg shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">BOM Linked</p>
                      <p className="text-xs text-green-600">Version {form.bomVersion ?? 'N/A'}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/manufacturing/bom?bomId=${form.bomId}`)}
                        className="h-7 px-3 rounded-md bg-white border border-green-300 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        View BOM
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/manufacturing/bom/new?itemId=${itemId || 'new'}&bomId=${form.bomId}`)}
                        className="h-7 px-3 rounded-md bg-[#4f46e5] text-xs font-medium text-white hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Update BOM
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                    <i className="ri-alert-line text-amber-500 text-lg shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">No BOM defined</p>
                      <p className="text-xs text-amber-600">
                        {isSubcontract
                          ? 'Required: the BOM lists the material sent to the sub-contractor. Create and activate it after saving the item.'
                          : 'Create a BOM to track raw materials and costs'}
                      </p>
                    </div>
                    {isEditing && itemId ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/manufacturing/bom/new?itemId=${itemId}`)}
                        className="h-7 px-3 rounded-md bg-[#4f46e5] text-xs font-medium text-white hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Create BOM
                      </button>
                    ) : (
                      <span className="text-xs text-amber-600 italic">Save item first</span>
                    )}
                  </div>
                )}
              </Section>
            )}

            {/* ── Variant Settings ── */}
            {(form.itemType === 'FINISHED_GOOD' || form.itemType === 'SEMI_FINISHED') && (
              <div className="border-t border-gray-100 pt-4 mt-4">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide mb-3">
                  Variant Settings
                </h3>

                <div className="flex items-start justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">
                      This product has variants
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Enable if this product comes in multiple models, sizes or configurations
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={hasVariants}
                    aria-label="This product has variants"
                    onClick={() => setHasVariants(!hasVariants)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 transition-colors duration-200 ease-in-out ml-4 mt-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 ${
                      hasVariants ? 'bg-indigo-600 border-indigo-600' : 'bg-gray-300 border-gray-400'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow border border-gray-300 ring-0 transition duration-200 ease-in-out ${
                        hasVariants ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {hasVariants && (
                  <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <i className="ri-information-line text-indigo-500 text-sm mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-indigo-700">
                          Variant Mode Active
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          <li className="text-xs text-indigo-600">
                            → This item becomes a Parent product
                          </li>
                          <li className="text-xs text-indigo-600">
                            → It will NOT be selectable directly in invoices
                          </li>
                          <li className="text-xs text-indigo-600">
                            → Only its variants appear in item search
                          </li>
                          <li className="text-xs text-indigo-600">
                            → Add variants from the Items list page using the [+ Add Variant] button
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ═══ TAB 3 — TRACKING ═══ */}
          <div className={activeTab === 'tracking' ? 'block space-y-5' : 'hidden'}>
            <Section title="Batch and Lot Tracking">
              <Toggle
                label="Enable Batch Tracking"
                checked={form.isBatchTracked ?? false}
                onChange={toggleBatch}
                helper="Recommended: food, pharma, chemicals, garments"
                icon="ri-stack-line"
              />
              <Toggle
                label="Enable Serial Tracking"
                checked={form.isSerialTracked ?? false}
                onChange={toggleSerial}
                helper="Recommended: electronics, machinery, vehicles"
                icon="ri-barcode-box-line"
              />
            </Section>

            {form.isSerialTracked && (() => {
              const currentYear = new Date().getFullYear();
              const prefixClean = effectivePrefix || 'SN';
              const sampleSerial1 = liveSerialPreview?.first || `SN-${prefixClean}-${currentYear}-0001`;
              const sampleSerial2 = liveSerialPreview?.second || `SN-${prefixClean}-${currentYear}-0002`;

              const candidateSuggestions: string[] = [];

              if (form.brand && form.brand.trim()) {
                const b = form.brand.trim().replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
                if (b.length >= 2) candidateSuggestions.push(b);
              }

              if (form.name && form.name.trim()) {
                const words = form.name.trim().split(/\s+/).filter(Boolean);
                if (words.length > 0) {
                  const w1 = words[0].replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
                  if (w1.length >= 2) candidateSuggestions.push(w1);

                  if (words.length > 1) {
                    const w2 = words[1].replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
                    if (w2.length >= 2) candidateSuggestions.push(w2);
                  }

                  const initials = words.map((w) => w[0]).join('').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 4);
                  if (initials.length >= 2) candidateSuggestions.push(initials);

                  const cleanFull = form.name.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
                  if (cleanFull.length >= 2) candidateSuggestions.push(cleanFull);
                }
              }

              if (form.code && form.code.trim()) {
                const uc = form.code.trim().toUpperCase();
                if (!uc.startsWith('ITM-') && !uc.startsWith('ITM')) {
                  const c = uc.replace(/[^A-Za-z0-9]/g, '').slice(0, 4);
                  if (c.length >= 2) candidateSuggestions.push(c);
                }
              }

              const suggestions = Array.from(new Set(candidateSuggestions))
                .filter((s) => s && s.length >= 2 && s !== form.serialPrefix);

              const opStock = Number(form.openingStock || 0);
              const warrantyMonths = Number(form.defaultWarrantyMonths || 0);

              const match = sampleSerial1.match(/^(SN-.*?-)(\d+)$/);
              const startSeq = match ? parseInt(match[2], 10) : 1;
              const padLen = match ? match[2].length : 4;
              const prefixPart = match ? match[1] : `SN-${prefixClean}-${currentYear}-`;
              const endSerial = `${prefixPart}${String(startSeq + opStock - 1).padStart(padLen, '0')}`;

              return (
                <Section title="Serial Number Tracking Configuration">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field
                        label={
                          <div className="flex items-center justify-between w-full">
                            <span>Serial Prefix</span>
                            <button
                              type="button"
                              onClick={() => {
                                setIsManualPrefix(false);
                                const autoPrefix = deriveSerialPrefix(form.name, form.code, form.brand);
                                update('serialPrefix', autoPrefix);
                              }}
                              className="text-[11px] font-normal text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer transition-colors"
                              title="Re-derive prefix dynamically from item name or brand"
                            >
                              <i className="ri-magic-line text-xs" /> Auto-derive
                            </button>
                          </div>
                        }
                      >
                        <input
                          type="text"
                          value={form.serialPrefix ?? ''}
                          onChange={(e) => {
                            setIsManualPrefix(true);
                            update('serialPrefix', e.target.value.toUpperCase());
                          }}
                          placeholder="e.g. LAP, MBL, EQ"
                          className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 font-mono uppercase"
                        />
                        {suggestions.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <span className="text-[11px] text-slate-400">Suggestions:</span>
                            {suggestions.map((sug) => (
                              <button
                                key={sug}
                                type="button"
                                onClick={() => {
                                  setIsManualPrefix(true);
                                  update('serialPrefix', sug);
                                }}
                                className="px-1.5 py-0.5 text-[10px] font-mono font-medium rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 cursor-pointer transition-colors"
                              >
                                {sug}
                              </button>
                            ))}
                          </div>
                        )}
                      </Field>

                      <Field label="Default Warranty (Months)">
                        <input
                          type="number"
                          min="0"
                          value={form.defaultWarrantyMonths ?? ''}
                          onChange={(e) => update('defaultWarrantyMonths', e.target.value === '' ? null : parseInt(e.target.value))}
                          placeholder="e.g. 12, 24, 36"
                          className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                        />
                        <p className="text-xs text-[#94a3b8]">
                          {warrantyMonths > 0
                            ? `Calculates ${warrantyMonths} months warranty from GRN receipt date`
                            : 'Set warranty duration in months (0 for no warranty)'}
                        </p>
                      </Field>
                    </div>

                    {/* Live Pattern & Preview Card */}
                    <div className="p-3.5 bg-blue-50/60 border border-blue-200/80 rounded-xl space-y-2.5">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <span className="text-xs font-semibold text-blue-900 flex items-center gap-1.5">
                          <i className="ri-qr-code-line text-blue-600" />
                           Sequence Generation Preview
                        </span>
                        <div className="flex items-center gap-2">
        
                          <span className="text-[11px] font-medium text-blue-700 bg-blue-100/70 px-2 py-0.5 rounded-full font-mono">
                            Format: SN-{"{PREFIX}"}-{"{YEAR}"}-{"{SEQ}"}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <div className="p-2.5 bg-white rounded-lg border border-blue-100 shadow-xs flex items-center justify-between">
                          <span className="text-slate-500">Next Available Serial:</span>
                          <span className="font-mono font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">{sampleSerial1}</span>
                        </div>
                        <div className="p-2.5 bg-white rounded-lg border border-blue-100 shadow-xs flex items-center justify-between">
                          <span className="text-slate-500">Following Serial:</span>
                          <span className="font-mono font-semibold text-slate-700 bg-slate-50 px-2 py-0.5 rounded border border-slate-200">{sampleSerial2}</span>
                        </div>
                      </div>

                
                       {opStock > 0 && (<span></span>
                        // <div className="mt-1 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-900 flex items-start gap-2">
                        //   <i className="ri-checkbox-circle-fill text-emerald-600 text-sm shrink-0 mt-0.5" />
                        //   {/* <div>
                        //     <span className="font-semibold">Opening Stock Serial Auto-Creation:</span>
                        //     <p className="text-emerald-800 mt-0.5">
                        //       Saving this item will automatically create <strong>{opStock}</strong> unique serial records ({sampleSerial1} to {endSerial}) in stock ready for sale.
                        //     </p>
                        //   </div> */}
                        // </div>
                      )} 
                    </div>
                  </div>
                </Section>
              );
            })()}

            {form.isBatchTracked && (
              <Section title="Expiry and Shelf Life">
                <Toggle
                  label="Has Expiry Date"
                  checked={form.hasExpiry ?? false}
                  onChange={() => update('hasExpiry', !form.hasExpiry)}
                  helper="Enable if items expire after a fixed period"
                  icon="ri-calendar-event-line"
                />
                {form.hasExpiry && (
                  <Field label="Shelf Life Days">
                    <input
                      type="number"
                      value={form.shelfLifeDays ?? ''}
                      onChange={(e) => update('shelfLifeDays', e.target.value === '' ? null : parseInt(e.target.value))}
                      placeholder="e.g. 365"
                      className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                    />
                    <p className="text-xs text-[#94a3b8]">Expiry = Receipt date + Shelf life days</p>
                  </Field>
                )}
              </Section>
            )}

            <Section title="Quality Control">
              <Toggle
                label="Requires Incoming QC"
                checked={form.requiresIncomingQC ?? false}
                onChange={() => update('requiresIncomingQC', !form.requiresIncomingQC)}
                helper="QC check triggered after GRN save · suggested ON for Buy / Subcontract"
                icon="ri-checkbox-circle-line"
              />
              <Toggle
                label="Requires Final QC"
                checked={form.requiresFinalQC ?? false}
                onChange={() => update('requiresFinalQC', !form.requiresFinalQC)}
                helper="QC check required before dispatch · suggested ON for Make / Subcontract"
                icon="ri-shield-check-line"
              />
            </Section>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#e2e8f0] shrink-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel (Esc)
            </button>
            {activeTab !== 'basic' && (
              <button
                type="button"
                onClick={() => setActiveTab(activeTab === 'tracking' ? 'manufacturing' : 'basic')}
                className="flex items-center gap-1 h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" /> Back
              </button>
            )}
            {activeTab !== 'tracking' && (
              <button
                type="button"
                onClick={handleNextTab}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-indigo-50 border border-indigo-200 text-[#4f46e5] text-sm font-semibold hover:bg-indigo-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Next <i className="ri-arrow-right-line" />
              </button>
            )}
            <button
              onClick={() => void handleSave()}
              disabled={isSaving || !!duplicateWarning}
              data-nav-index={12}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  void handleSave();
                }
              }}
              className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/40"
            >
              {isSaving ? (
                <>
                  <i className="ri-loader-4-line animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <i className="ri-save-line" /> {isEditing ? 'Update Item' : 'Save Item'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
